import { useState, useEffect, useRef, useCallback } from 'react';

const _rc = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
const PRIMARY_API = _rc.PRIMARY_API_URL || process.env.REACT_APP_PRIMARY_API_URL || 'https://premium-watch-store-3.preview.emergentagent.com';
const WS_BASE = PRIMARY_API.replace('https://', 'wss://').replace('http://', 'ws://');

const getToken = () => {
  try {
    const raw = sessionStorage.getItem('vwatch_admin_auth');
    return raw ? JSON.parse(raw).token : null;
  } catch (e) { console.warn('Token parse error:', e.message); return null; }
};

export function useMessagingWebSocket(onNewMessage, onConversationUpdate, onCustomerCleared) {
  const [status, setStatus] = useState('Connecting');
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const onNewMessageRef = useRef(onNewMessage);
  const onConversationUpdateRef = useRef(onConversationUpdate);
  const onCustomerClearedRef = useRef(onCustomerCleared);

  // Keep refs in sync
  useEffect(() => { onNewMessageRef.current = onNewMessage; }, [onNewMessage]);
  useEffect(() => { onConversationUpdateRef.current = onConversationUpdate; }, [onConversationUpdate]);
  useEffect(() => { onCustomerClearedRef.current = onCustomerCleared; }, [onCustomerCleared]);

  const connect = useCallback(() => {
    const token = getToken();
    if (!token || !WS_BASE) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) return;

    const url = `${WS_BASE}/api/ws?token=${encodeURIComponent(token)}`;
    setStatus('Connecting');

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => { setConnected(true); setStatus('Live'); };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'connected') return;
          if (data.type === 'new_message' && data.message) {
            onNewMessageRef.current?.(data);
          } else if (data.type === 'conversation_update') {
            onConversationUpdateRef.current?.();
          } else if (data.type === 'customer_cleared') {
            onCustomerClearedRef.current?.(data);
          }
        } catch (e) { console.warn('WS parse error:', e.message); }
      };

      ws.onclose = () => {
        setConnected(false); setStatus('Offline'); wsRef.current = null;
        reconnectTimer.current = setTimeout(connect, 5000);
      };
      ws.onerror = () => { /* onclose fires after */ };
    } catch (e) {
      setConnected(false); setStatus('Offline');
      reconnectTimer.current = setTimeout(connect, 5000);
    }
  }, []);

  useEffect(() => {
    connect();
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try { wsRef.current.send(JSON.stringify({ type: 'ping' })); }
        catch (e) { console.warn('WS ping failed:', e.message); }
      }
    }, 25000);
    return () => {
      clearInterval(ping);
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* expected on unmount */ } }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  return { status, connected };
}
